import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Monitor, Stethoscope, AlertTriangle, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { format, differenceInDays } from "date-fns";

interface PendingStudent {
  id: string;
  name: string;
  issue: string;
  status: string;
  days_pending?: number;
  appointment_date?: string;
}

export const PendingItemsTrackerWidget = () => {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["pending-items-tracker"],
    queryFn: async () => {
      const { data: students, error } = await supabase
        .from("students")
        .select("id, first_name, last_name, computer_number, computer_number_status, class_1_medical, class_2_medical, class1_appointment_date, class2_appointment_date, date_of_joining");

      if (error) throw error;

      const computerPending: PendingStudent[] = [];
      const medicalPending: PendingStudent[] = [];

      students?.forEach(student => {
        const name = `${student.first_name} ${student.last_name || ""}`.trim();
        const joinDate = new Date(student.date_of_joining);
        const daysSinceJoin = differenceInDays(new Date(), joinDate);

        // Computer number tracking
        if (!student.computer_number || student.computer_number_status !== "Approved") {
          computerPending.push({
            id: student.id,
            name,
            issue: "Computer Number",
            status: student.computer_number_status || "Pending",
            days_pending: daysSinceJoin,
          });
        }

        // Class 2 Medical tracking (prerequisite for Class 1)
        if (student.class_2_medical !== "Done") {
          medicalPending.push({
            id: student.id,
            name,
            issue: "Class 2 Medical",
            status: student.class_2_medical,
            days_pending: daysSinceJoin,
            appointment_date: student.class2_appointment_date || undefined,
          });
        }

        // Class 1 Medical tracking (only if Class 2 is done)
        if (student.class_2_medical === "Done" && student.class_1_medical !== "Done") {
          medicalPending.push({
            id: student.id,
            name,
            issue: "Class 1 Medical",
            status: student.class_1_medical,
            days_pending: daysSinceJoin,
            appointment_date: student.class1_appointment_date || undefined,
          });
        }
      });

      return {
        computerPending: computerPending.sort((a, b) => (b.days_pending || 0) - (a.days_pending || 0)),
        medicalPending: medicalPending.sort((a, b) => (b.days_pending || 0) - (a.days_pending || 0)),
      };
    },
    staleTime: 2 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "Pending":
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">Pending</Badge>;
      case "Applied":
        return <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">Applied</Badge>;
      case "Verification Pending":
        return <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-500/20">Verification</Badge>;
      case "Approved":
        return <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">Approved</Badge>;
      case "Appointment Scheduled":
        return <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-500/20">Scheduled</Badge>;
      case "Test Done":
        return <Badge variant="outline" className="bg-cyan-500/10 text-cyan-600 border-cyan-500/20">Test Done</Badge>;
      case "Retest":
        return <Badge variant="destructive">Retest</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-primary" />
          Pending Items Tracker
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="computer" className="w-full">
          <TabsList className="w-full grid grid-cols-2">
            <TabsTrigger value="computer" className="text-xs">
              <Monitor className="h-3 w-3 mr-1" />
              Computer ({data?.computerPending.length || 0})
            </TabsTrigger>
            <TabsTrigger value="medical" className="text-xs">
              <Stethoscope className="h-3 w-3 mr-1" />
              Medical ({data?.medicalPending.length || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="computer" className="mt-3">
            {data?.computerPending.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                All students have approved computer numbers
              </p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {data?.computerPending.slice(0, 10).map(item => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-2 rounded-lg border hover:bg-muted/50 cursor-pointer"
                    onClick={() => navigate(`/students/${item.id}`)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.days_pending} days since joining
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(item.status)}
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
                {(data?.computerPending.length || 0) > 10 && (
                  <Button 
                    variant="ghost" 
                    className="w-full text-xs"
                    onClick={() => navigate("/students")}
                  >
                    View all {data?.computerPending.length} pending
                  </Button>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="medical" className="mt-3">
            {data?.medicalPending.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                All students have completed medicals
              </p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {data?.medicalPending.slice(0, 10).map((item, idx) => (
                  <div
                    key={`${item.id}-${idx}`}
                    className="flex items-center justify-between p-2 rounded-lg border hover:bg-muted/50 cursor-pointer"
                    onClick={() => navigate(`/students/${item.id}`)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.issue}
                        {item.appointment_date && (
                          <> • Appt: {format(new Date(item.appointment_date), "MMM d")}</>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(item.status)}
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
                {(data?.medicalPending.length || 0) > 10 && (
                  <Button 
                    variant="ghost" 
                    className="w-full text-xs"
                    onClick={() => navigate("/students")}
                  >
                    View all {data?.medicalPending.length} pending
                  </Button>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
